import React, { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";
import HomeLogoLink from "./HomeLogoLink";
import { clearAuthToken } from "@/lib/authToken";
import { useAuthSession } from "@/hooks/useAuthSession";

export default function TopNav({ onSignIn, onSignUp }) {
    const location = useLocation();
    const isStudio = location.pathname === "/studio";

    return (
        <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-black/80 backdrop-blur-xl">
            <div className="mx-auto flex h-20 max-w-[1500px] items-center justify-between gap-3 px-4 sm:px-6 lg:px-10">
                <div className="flex min-w-0 items-center gap-6 lg:gap-10">
                    <HomeLogoLink>
                        <Logo size={32} />
                    </HomeLogoLink>
                    <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
                        <NavLink to="/how-it-works" label="How It Works" />
                        <NavLink to="/examples" label="Examples" />
                        <NavLink to="/pricing" label="Pricing" />
                        <NavLink to="/resources" label="Resources" />
                    </nav>
                </div>

                <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                    <AccountNav onSignIn={onSignIn} onSignUp={onSignUp} />
                    {!isStudio && (
                        <Link to="/studio" className="fsrs-cta hidden sm:flex">
                            Open Studio
                        </Link>
                    )}
                </div>
            </div>
        </header>
    );
}

function AccountNav({ onSignIn, onSignUp }) {
    const session = useAuthSession();

    return (
        <nav aria-label="Account" className="flex items-center gap-2">
            {session ? (
                <AccountMenu email={session.email} />
            ) : (
                <>
                    <button
                        type="button"
                        onClick={onSignIn}
                        className="fsrs-auth-sign-in"
                        aria-label="Sign in"
                        aria-haspopup="dialog"
                        data-testid="header-sign-in"
                    >
                        Sign in
                    </button>
                    <button
                        type="button"
                        onClick={onSignUp}
                        className="fsrs-auth-sign-up"
                        aria-label="Sign up"
                        aria-haspopup="dialog"
                        data-testid="header-sign-up"
                    >
                        Sign up
                    </button>
                </>
            )}
        </nav>
    );
}

function AccountMenu({ email }) {
    const navigate = useNavigate();
    const location = useLocation();
    const menuId = useId();
    const rootRef = useRef(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (!rootRef.current?.contains(event.target)) setOpen(false);
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    const signOut = () => {
        setOpen(false);
        clearAuthToken();
        navigate("/");
    };

    return (
        <div className="fsrs-account" ref={rootRef}>
            <button
                type="button"
                className="fsrs-account-trigger"
                aria-label={`Account menu for ${email}`}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={menuId}
                title={email}
                data-testid="header-account"
                onClick={() => setOpen((value) => !value)}
            >
                <span className="fsrs-account-email" data-testid="header-account-email">
                    {email}
                </span>
                <span className="fsrs-account-chevron" aria-hidden="true" />
            </button>
            {open && (
                <div
                    id={menuId}
                    role="menu"
                    aria-label="Account menu"
                    className="fsrs-account-menu"
                    data-testid="header-account-menu"
                >
                    <Link
                        role="menuitem"
                        to="/studio"
                        className="fsrs-account-item"
                        data-testid="header-account-studio"
                        onClick={() => setOpen(false)}
                    >
                        Open Studio
                    </Link>
                    <button
                        type="button"
                        role="menuitem"
                        className="fsrs-account-item"
                        data-testid="header-account-sign-out"
                        onClick={signOut}
                    >
                        Sign out
                    </button>
                </div>
            )}
        </div>
    );
}

function NavLink({ to, label }) {
    const location = useLocation();
    const active = location.pathname === to;
    return (
        <Link
            to={to}
            className={`fsrs-mono text-[13px] font-bold tracking-widest transition-colors hover:text-red-500 ${active ? "text-red-500" : "text-slate-400"}`}
        >
            {label.toUpperCase()}
        </Link>
    );
}
